import type { Ref } from "vue";
import { ref, watch } from "vue";

import type {
  AnyAgentId,
  AutomationId,
  Controller,
  OrgId,
  PageId,
  ReferenceId,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import type {
  ObjectContext,
  UseAutomations,
} from "@/components/ai/useAutomations";
import type { BaseLogger as Logger } from "@/components/inject";
import { getOrganizationIdFromWorkspaceId } from "@/components/workspaces";
import type { UseProfile } from "@/composables/useProfile";

export type PaletteContext =
  | {
      organizationId?: OrgId;
      workspaceId: WorkspaceId;
      kind: "object";
      tableId: string;
      rowId: string;
    }
  | {
      organizationId?: OrgId;
      workspaceId: WorkspaceId;
      kind: "page";
      pageId: PageId;
    }
  // Default objects (task/event/people) live in the personal space and
  // already carry a `ReferenceId`; no workspace is needed to resolve their
  // organization (it is the personal one).
  | {
      organizationId?: OrgId;
      kind: "reference";
      referenceId: ReferenceId;
    }
  | null;

export type Palette = {
  context: Ref<PaletteContext>;
  automations: Ref<UseAutomations | undefined>;
  open: (ctx?: PaletteContext) => void;
  triggerAutomation: (
    organizationId: OrgId,
    agentId: AnyAgentId | null,
    automationId: AutomationId
  ) => Promise<void>;
  /** Stops the automations debug watcher; callers own cleanup. */
  release: () => void;
};

export function createPalette(
  controller: Controller,
  baseLogger: Logger,
  profile: Ref<UseProfile>,
  openModal: () => void,
  automations: Ref<UseAutomations | undefined>
): Palette {
  const logger = baseLogger.forModule("palette");
  const context = ref<PaletteContext>(null);

  const stopWatchAutomations = watch(
    automations,
    (newVal) => {
      logger.debug("automations", newVal);
    },
    { immediate: true }
  );

  const open = (ctx?: PaletteContext) => {
    const next = ctx ?? null;

    if (next && !next.organizationId) {
      const organizationId =
        next.kind === "reference"
          ? profile.value.user.personal.organizationId
          : getOrganizationIdFromWorkspaceId(profile.value, next.workspaceId);
      context.value = { ...next, organizationId };
    } else {
      context.value = next;
    }

    openModal();
  };

  const triggerAutomation = async (
    organizationId: OrgId,
    agentId: AnyAgentId | null,
    automationId: AutomationId
  ) => {
    logger.debug("triggerAutomation()", {
      organizationId,
      agentId,
      automationId,
    });
    let objectContext: ObjectContext | undefined;
    if (context.value?.kind === "object") {
      const rawId = await controller.graph.object.id.make({
        workspace_id: context.value.workspaceId,
        table_id: context.value.tableId,
        row_id: context.value.rowId,
      });
      objectContext = {
        objectId: { kind: "object", id: rawId } satisfies ReferenceId,
      };
    } else if (context.value?.kind === "page") {
      objectContext = {
        objectId: {
          kind: "page",
          id: context.value.pageId,
        } satisfies ReferenceId,
      };
    } else if (context.value?.kind === "reference") {
      objectContext = { objectId: context.value.referenceId };
    }
    const response = await automations.value?.run(
      organizationId,
      agentId,
      automationId,
      objectContext
    );
    logger.debug("triggerAutomation response", response);
    // XXX use _response e.g show a notification, show a modal, etc.
  };

  const release = () => {
    stopWatchAutomations();
  };

  return { context, automations, open, triggerAutomation, release };
}
